/**
 * The typed client for the first-party team/user MANAGEMENT API (controlplane-auth task 13.2;
 * requirements A4). The account/admin screens read and mutate the current team's membership through
 * these calls; they are the UI half of the management surface the BFF forwards to `core` (task 9).
 *
 * All four endpoints live UNDER `/api/v1` (the operator-API surface the BFF proxies), so this module
 * builds on the shared {@link apiFetch} wrapper (`lib/api.ts`) — same origin, cookie-borne session,
 * the same {@link ApiError} classes. It does NOT use the top-level `/auth/*` wrapper
 * (`lib/auth-client.ts`), which owns a different surface.
 *
 * TENANCY (Property 5 / A8): no call carries a tenant id. The server scopes every read and write to
 * the SESSION's selected tenant (the minted envelope's `tid`), so a screen can only ever see or
 * change the current team's members. The member id in a PATCH/DELETE path is interpreted by `core`
 * within that tenant; a foreign id simply matches nothing (see `index-management.test.ts`).
 *
 * RBAC: the writes are admin-only. `core` is the authority and refuses a non-admin write with 403
 * regardless of the UI. The screens ALSO hide the write affordances from non-admins (a client-side
 * convenience, task 13.2) — but this module makes no such assumption; it faithfully performs the
 * call and surfaces whatever `core` decides.
 *
 * ENVELOPE: management responses use the AgentDNA `{ status, message, data }` convention (design
 * §admin-server data/API conventions). This module reads the typed payload out of `data` and shapes
 * it defensively, so a malformed body degrades to an empty/clear result rather than crashing a
 * screen (fail closed), mirroring `parseSessionData` in `AuthContext`.
 *
 * BACKEND-ROUTING GAP (Phase 7 / task 15, documented in task 12): the `/api/v1/team/*` forwards are
 * implemented in the BFF (`management.ts`) and routed in `index.ts` for the session path, but the
 * full cutover that makes session validation the only `/api/*` path lands in task 15. These screens
 * call the correct endpoints via `apiFetch` today; until the cutover an unrouted/Access-gated call
 * surfaces as an `ApiError` the screen shows honestly (not a crash).
 */

import { apiFetch, ApiError } from "./api.ts";
import { type OperatorRole } from "@oathera/contract/rbac";

/** The roles a member may hold within a team (the operator ladder). Re-exported for the screens. */
export type MemberRole = OperatorRole;

/** The closed set of membership statuses a member row may carry. */
export type MemberStatus = "active" | "invited" | "suspended";

/** A team member as the management screens consume it (shaped from the server `data`). */
export interface TeamMember {
  /** The opaque user id (the envelope `sub`); the stable key and the id used in PATCH/DELETE paths. */
  readonly id: string;
  /** The member's email, for display. */
  readonly email: string;
  /** An optional friendly name. */
  readonly displayName?: string;
  /** The member's role in the current team. */
  readonly role: MemberRole;
  /** The membership status; defaults to `active` when the server omits it. */
  readonly status: MemberStatus;
  /** When the member last signed in (epoch ms), when the server provides it. */
  readonly lastSeenAt?: number;
}

/** The response envelope the management API returns (`{ status, message, data }`). */
interface ManagementEnvelope<T> {
  readonly status?: boolean;
  readonly message?: string;
  readonly data?: T | null;
}

const KNOWN_ROLES: readonly MemberRole[] = ["viewer", "operator", "admin"];
const KNOWN_STATUS: readonly MemberStatus[] = ["active", "invited", "suspended"];

function asRole(value: unknown): MemberRole | null {
  return typeof value === "string" && (KNOWN_ROLES as readonly string[]).includes(value)
    ? (value as MemberRole)
    : null;
}

function asStatus(value: unknown): MemberStatus {
  return typeof value === "string" && (KNOWN_STATUS as readonly string[]).includes(value)
    ? (value as MemberStatus)
    : "active";
}

/**
 * Shape one raw member row into a {@link TeamMember}, or `null` when it has no usable id/role. Pure
 * and defensive: a malformed row is dropped rather than crashing the table.
 */
function parseMember(raw: unknown): TeamMember | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const id = r["id"];
  if (typeof id !== "string" || id.length === 0) return null;
  const role = asRole(r["role"]);
  if (role === null) return null;
  const displayName = r["display_name"];
  const lastSeen = r["last_seen_at"];
  return {
    id,
    email: typeof r["email"] === "string" ? (r["email"] as string) : "",
    role,
    status: asStatus(r["status"]),
    ...(typeof displayName === "string" ? { displayName } : {}),
    ...(typeof lastSeen === "number" && Number.isFinite(lastSeen) ? { lastSeenAt: lastSeen } : {}),
  };
}

/**
 * Pull the member array out of a management `data` payload. The server may answer either
 * `{ data: { members: [...] } }` or `{ data: [...] }`; both are accepted. Unusable rows are dropped.
 */
export function parseMembers(data: unknown): readonly TeamMember[] {
  let rows: readonly unknown[] = [];
  if (Array.isArray(data)) {
    rows = data;
  } else if (typeof data === "object" && data !== null) {
    const members = (data as { members?: unknown }).members;
    if (Array.isArray(members)) rows = members;
  }
  const out: TeamMember[] = [];
  for (const row of rows) {
    const member = parseMember(row);
    if (member !== null) out.push(member);
  }
  return out;
}

/** List the current team's members (`GET /api/v1/team/members`). Scoped to the session tenant. */
export async function listMembers(signal?: AbortSignal): Promise<readonly TeamMember[]> {
  const body = await apiFetch<ManagementEnvelope<unknown>>("/team/members", {
    ...(signal === undefined ? {} : { signal }),
  });
  return parseMembers(body?.data ?? null);
}

/** The body for an invite (`POST /api/v1/team/invites`). */
export interface InviteInput {
  /** The invitee's email. */
  readonly email: string;
  /** The role to grant on redemption. */
  readonly role: MemberRole;
}

/** Invite/add an operator to the current team (admin-only; `core` enforces). */
export async function inviteMember(input: InviteInput, signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>("/team/invites", {
    method: "POST",
    body: { email: input.email, role: input.role },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Change a member's role (`PATCH /api/v1/team/members/:id`; admin-only). */
export async function changeMemberRole(
  memberId: string,
  role: MemberRole,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>(`/team/members/${encodeURIComponent(memberId)}`, {
    method: "PATCH",
    body: { role },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Remove a member from the current team (`DELETE /api/v1/team/members/:id`; admin-only). */
export async function removeMember(memberId: string, signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>(`/team/members/${encodeURIComponent(memberId)}`, {
    method: "DELETE",
    ...(signal === undefined ? {} : { signal }),
  });
}

/** The stable query key for the members list, so a screen's refetch after a write is consistent. */
export const TEAM_MEMBERS_KEY = ["team", "members"] as const;

/** Re-export so screens can branch on error kind without re-importing from `lib/api.ts`. */
export { ApiError };

// -------------------------------------------------------------------------------------------------
// Superadmin: teams (tenants / organizations)
// -------------------------------------------------------------------------------------------------

/** A team (tenant/organization) row as the superadmin Teams screen consumes it. */
export interface Team {
  readonly id: string;
  readonly name: string;
  readonly orgLegalName: string | null;
  readonly orgDomain: string | null;
  readonly orgContactEmail: string | null;
  readonly isRoot: boolean;
  readonly memberCount: number;
}

function parseTeam(raw: unknown): Team | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r["id"] !== "string" || typeof r["name"] !== "string") return null;
  const str = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null);
  return {
    id: r["id"],
    name: r["name"],
    orgLegalName: str(r["org_legal_name"]),
    orgDomain: str(r["org_domain"]),
    orgContactEmail: str(r["org_contact_email"]),
    isRoot: r["is_root"] === true,
    memberCount: typeof r["member_count"] === "number" ? r["member_count"] : 0,
  };
}

/** List every team (superadmin only; `GET /api/v1/teams`). */
export async function listTeams(signal?: AbortSignal): Promise<readonly Team[]> {
  const body = await apiFetch<ManagementEnvelope<unknown>>("/teams", {
    ...(signal === undefined ? {} : { signal }),
  });
  const data = body?.data;
  const rows = typeof data === "object" && data !== null ? (data as { teams?: unknown }).teams : null;
  if (!Array.isArray(rows)) return [];
  const out: Team[] = [];
  for (const row of rows) {
    const t = parseTeam(row);
    if (t !== null) out.push(t);
  }
  return out;
}

/** The body for creating a team (superadmin only; `POST /api/v1/teams`). */
export interface CreateTeamInput {
  readonly name: string;
  readonly ownerEmail: string;
  readonly orgLegalName?: string;
  readonly orgDomain?: string;
  readonly orgContactEmail?: string;
}

/**
 * Create a team with a mandatory owner (superadmin only). When the acting operator is enrolled in
 * TOTP, the server requires step-up MFA: pass the fresh 6-digit `mfaCode`. Without it (or with a bad
 * one) the server replies 401 and {@link isMfaRequiredError} is true, so the caller can prompt and
 * retry with the code.
 */
export async function createTeam(input: CreateTeamInput, mfaCode?: string, signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>("/teams", {
    method: "POST",
    body: {
      name: input.name,
      owner_email: input.ownerEmail,
      ...(input.orgLegalName !== undefined ? { org_legal_name: input.orgLegalName } : {}),
      ...(input.orgDomain !== undefined ? { org_domain: input.orgDomain } : {}),
      ...(input.orgContactEmail !== undefined ? { org_contact_email: input.orgContactEmail } : {}),
      ...(mfaCode !== undefined && mfaCode !== "" ? { mfa_code: mfaCode } : {}),
    },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** The editable fields of a team (superadmin only; `PATCH /api/v1/teams/{id}`). Owner is NOT here. */
export interface UpdateTeamInput {
  readonly name: string;
  readonly orgLegalName?: string;
  readonly orgDomain?: string;
  readonly orgContactEmail?: string;
}

/**
 * Edit a team's editable fields (superadmin only; `PATCH /api/v1/teams/{id}`). Only the team name,
 * organization legal name, organization domain and organization contact email are editable; the
 * owner, root status and verification are untouched. Unlike {@link createTeam}, no step-up MFA is
 * required (an edit is a lower-risk field update). A 409 carries the offending `field` in the error
 * body so the caller can highlight the input (surfaced via `ApiError.data`).
 */
export async function updateTeam(teamId: string, input: UpdateTeamInput, signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>(`/teams/${encodeURIComponent(teamId)}`, {
    method: "PATCH",
    body: {
      name: input.name,
      ...(input.orgLegalName !== undefined ? { org_legal_name: input.orgLegalName } : {}),
      ...(input.orgDomain !== undefined ? { org_domain: input.orgDomain } : {}),
      ...(input.orgContactEmail !== undefined ? { org_contact_email: input.orgContactEmail } : {}),
    },
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Delete a team (superadmin only; `DELETE /api/v1/teams/{id}`). The server deletes the tenant and
 * only its auth/BFF-owned rows in one scoped, children-first batch and REFUSES a team that still has
 * operational data (409) or the root team (403) — both surface as an {@link ApiError} the caller
 * messages (`error.status` 409/403, `error.message` the server's explanation). No step-up MFA is
 * required (the client confirm dialog is the UI-side guard). Callers should invalidate
 * {@link TEAMS_KEY} on success.
 */
export async function deleteTeam(teamId: string, signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>(`/teams/${encodeURIComponent(teamId)}`, {
    method: "DELETE",
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * True when an error from a privileged write is the server's step-up-MFA challenge (401 carrying
 * `data.mfa_required`), i.e. the operator must supply a fresh TOTP code and retry.
 */
export function isMfaRequiredError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401 && error.code === "mfa_required";
}

/** The stable query key for the teams list. */
export const TEAMS_KEY = ["teams"] as const;

/** The unique fields the create-team form validates for availability in realtime. */
export type TeamUniqueField = "name" | "ownerEmail" | "orgLegalName" | "orgDomain";

/**
 * Realtime availability check for a single create-team field (superadmin only;
 * `GET /api/v1/teams/check?field=&value=`). Returns `true` when the value is free, `false` when it
 * already exists (so the form can show an inline error on blur). The server's `createTeam` re-checks
 * with the same logic, so this is a convenience, never the authority. A transport/HTTP error is
 * rethrown as an {@link ApiError} for the caller to handle (we do NOT silently claim "available").
 */
export async function checkTeamFieldAvailability(
  field: TeamUniqueField,
  value: string,
  signal?: AbortSignal,
): Promise<boolean> {
  const qs = `field=${encodeURIComponent(field)}&value=${encodeURIComponent(value)}`;
  const body = await apiFetch<ManagementEnvelope<{ field?: string; available?: boolean }>>(
    `/teams/check?${qs}`,
    { ...(signal === undefined ? {} : { signal }) },
  );
  // Fail safe: only treat an explicit `available: true` as free; anything else → not available.
  return body?.data?.available === true;
}

// -------------------------------------------------------------------------------------------------
// Organization profile + verification (the current team's organization)
// -------------------------------------------------------------------------------------------------

export type OrgEntityType =
  | "corporation"
  | "llc"
  | "partnership"
  | "nonprofit"
  | "sole_proprietorship"
  | "other";

export type OrgVerificationStatus =
  | "not_verified"
  | "verification_pending"
  | "verified"
  | "verification_failed"
  | "verification_expired"
  | "verification_revoked";

/**
 * The assurance state of the current organization credential the Members page renders as a badge
 * (org-credential-lifecycle Req 8.1). It is the user-facing projection of {@link OrgVerificationStatus}
 * onto the closed four-state set the design's UI section names:
 *   - `unverified` — a current credential exists carrying `kyc_status: "unverified"` (`not_verified`);
 *   - `pending`    — a register/verify transition is running (`verification_pending`);
 *   - `verified`   — the current credential is a verified credential (`verified`);
 *   - `failed`     — the last verification did not succeed (`verification_failed`/`_expired`/`_revoked`).
 */
export type OrgAssurance = "unverified" | "pending" | "verified" | "failed";

/**
 * Project an {@link OrgVerificationStatus} onto the four-state {@link OrgAssurance} badge the Members
 * page shows next to the credential id (Req 8.1). Pure; the failure-family states collapse to `failed`.
 */
export function assuranceFromStatus(status: OrgVerificationStatus): OrgAssurance {
  switch (status) {
    case "verified":
      return "verified";
    case "verification_pending":
      return "pending";
    case "verification_failed":
    case "verification_expired":
    case "verification_revoked":
      return "failed";
    case "not_verified":
    default:
      return "unverified";
  }
}

/**
 * A short, masked form of an organization credential id for display (Req 8.1). The credential id is
 * public VC reference data (never a secret; Req 8.4), but it is long, so the Members page renders a
 * head…tail form rather than the full value. Returns `null` when there is no id. Short ids (≤ 12
 * chars) are returned unchanged.
 */
export function maskCredentialId(credentialId: string | null): string | null {
  if (credentialId === null || credentialId.length === 0) return null;
  if (credentialId.length <= 12) return credentialId;
  return `${credentialId.slice(0, 6)}…${credentialId.slice(-4)}`;
}

/** The current team's organization record (profile + owner relationship + verification). */
export interface Organization {
  readonly organizationId: string;
  readonly createdAt: number;
  readonly isRoot: boolean;
  readonly name: string;
  readonly legalName: string | null;
  readonly displayName: string | null;
  readonly entityType: OrgEntityType | null;
  readonly country: string | null;
  readonly jurisdiction: string | null;
  readonly registrationNumber: string | null;
  readonly registeredAddress: string | null;
  readonly website: string | null;
  readonly businessEmail: string | null;
  readonly businessPhone: string | null;
  readonly industry: string | null;
  readonly ownerEmail: string | null;
  readonly ownerEmailVerified: boolean;
  readonly verificationStatus: OrgVerificationStatus;
  readonly verificationRequestedAt: number | null;
  readonly verificationCompletedAt: number | null;
  /** The current org credential's stable id (the VCI `credentialStatus.id`), `null` when none. */
  readonly credentialId: string | null;
  /**
   * The current org credential's assurance state (org-credential-lifecycle Req 8.1), derived from
   * {@link verificationStatus}. The Members page renders this as the badge; it is `unverified` when
   * no verification has been run (or no credential exists yet).
   */
  readonly assurance: OrgAssurance;
  /**
   * A short, masked form of {@link credentialId} for display (Req 8.1/8.4), or `null` when no
   * credential exists. The full id stays on {@link credentialId} for anything that needs it.
   */
  readonly credentialIdShort: string | null;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function parseOrg(data: unknown): Organization | null {
  if (typeof data !== "object" || data === null) return null;
  const r = (data as { org?: unknown }).org;
  if (typeof r !== "object" || r === null) return null;
  const o = r as Record<string, unknown>;
  if (typeof o["organization_id"] !== "string") return null;
  const status = o["verification_status"];
  const vs: OrgVerificationStatus =
    status === "verification_pending" || status === "verified" || status === "verification_failed" ||
    status === "verification_expired" || status === "verification_revoked"
      ? status
      : "not_verified";
  const et = o["org_entity_type"];
  const entity = (["corporation", "llc", "partnership", "nonprofit", "sole_proprietorship", "other"] as const).includes(et as OrgEntityType)
    ? (et as OrgEntityType)
    : null;
  return {
    organizationId: o["organization_id"] as string,
    createdAt: num(o["created_at"]) ?? 0,
    isRoot: o["is_root"] === true,
    name: typeof o["name"] === "string" ? (o["name"] as string) : "",
    legalName: str(o["org_legal_name"]),
    displayName: str(o["org_display_name"]),
    entityType: entity,
    country: str(o["org_country"]),
    jurisdiction: str(o["org_jurisdiction"]),
    registrationNumber: str(o["org_registration_number"]),
    registeredAddress: str(o["org_registered_address"]),
    website: str(o["org_website"]),
    businessEmail: str(o["org_business_email"]),
    businessPhone: str(o["org_business_phone"]),
    industry: str(o["org_industry"]),
    ownerEmail: str(o["owner_email"]),
    ownerEmailVerified: o["owner_email_verified"] === true,
    verificationStatus: vs,
    verificationRequestedAt: num(o["verification_requested_at"]),
    verificationCompletedAt: num(o["verification_completed_at"]),
    credentialId: str(o["credential_id"]),
    assurance: assuranceFromStatus(vs),
    credentialIdShort: maskCredentialId(str(o["credential_id"])),
  };
}

/** Read the current team's organization (`GET /api/v1/team/org`). */
export async function getOrganization(signal?: AbortSignal): Promise<Organization | null> {
  const body = await apiFetch<ManagementEnvelope<unknown>>("/team/org", {
    ...(signal === undefined ? {} : { signal }),
  });
  return parseOrg(body?.data ?? null);
}

/** Editable organization profile fields (admin only; `PATCH /api/v1/team/org`). */
export interface OrgProfileInput {
  readonly legalName?: string;
  readonly displayName?: string;
  readonly entityType?: OrgEntityType | "";
  readonly country?: string;
  readonly jurisdiction?: string;
  readonly registrationNumber?: string;
  readonly registeredAddress?: string;
  readonly website?: string;
  readonly businessEmail?: string;
  readonly businessPhone?: string;
  readonly industry?: string;
}

/** Update the current team's organization profile (admin only). */
export async function updateOrganization(input: OrgProfileInput, signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>("/team/org", {
    method: "PATCH",
    body: {
      org_legal_name: input.legalName ?? "",
      org_display_name: input.displayName ?? "",
      org_entity_type: input.entityType ?? "",
      org_country: input.country ?? "",
      org_jurisdiction: input.jurisdiction ?? "",
      org_registration_number: input.registrationNumber ?? "",
      org_registered_address: input.registeredAddress ?? "",
      org_website: input.website ?? "",
      org_business_email: input.businessEmail ?? "",
      org_business_phone: input.businessPhone ?? "",
      org_industry: input.industry ?? "",
    },
    ...(signal === undefined ? {} : { signal }),
  });
}

// -------------------------------------------------------------------------------------------------
// Organization credential lifecycle (org-credential-lifecycle): register / revoke-and-reissue.
// -------------------------------------------------------------------------------------------------

/**
 * The outcome of registering (or re-registering) the organization credential, as the Members page
 * consumes it (shaped from the BFF `POST /api/v1/team/org/credential` response; design "BFF
 * endpoints"). The claims come from the persisted org profile, never the request body, so there is
 * no input shape — the call carries only the session envelope.
 *
 *   - `credentialId`   — the new current credential's stable id (the VCI `credentialStatus.id`).
 *   - `kycStatus`      — the issued credential's assurance claim; always `"unverified"` on register
 *                        (a verified credential is only minted via {@link applyForOrgVerification}).
 *   - `verificationStatus` — the synced read-model status core committed (`not_verified` on register).
 *   - `reissued`       — `true` when a prior current credential was revoked and replaced, `false`
 *                        when this is the first issuance (drives the outcome toast wording, Req 8.3).
 *   - `revokedPriorId` — the id of the credential that was revoked on a reissue, or `null` on a
 *                        first issuance.
 */
export interface RegisterOrgCredentialResult {
  readonly credentialId: string;
  readonly kycStatus: "unverified";
  readonly verificationStatus: OrgVerificationStatus;
  readonly reissued: boolean;
  readonly revokedPriorId: string | null;
}

/**
 * Register the organization credential for the current team (admin only;
 * `POST /api/v1/team/org/credential`). Issues an unverified `OrganizationSponsorCredential`, or
 * revokes the current one and issues a fresh unverified one when a current credential already exists
 * (the design's issue / revoke-and-reissue path; Req 2.1, 3.1). The body is empty: core projects the
 * authoritative claims from the persisted org profile (Req 7.7), so this call carries only the
 * session envelope.
 *
 * Resolves to a typed {@link RegisterOrgCredentialResult}. Failures surface as an {@link ApiError}
 * the caller branches on by `status`/`code` (mirroring {@link applyForOrgVerification}):
 *   - 400 `invalid_org_profile` — required profile fields are missing/invalid; no state changed (Req 2.4);
 *   - 409 `org_credential_contended` — a concurrent transition; retryable;
 *   - 503 `issuance_unavailable` — AIS/VCI/commit failed; the prior credential (if any) is intact (Req 2.5).
 */
export async function registerOrgCredential(signal?: AbortSignal): Promise<RegisterOrgCredentialResult> {
  const body = await apiFetch<ManagementEnvelope<{
    credential_id?: string; kyc_status?: string; verification_status?: string;
    reissued?: boolean; revoked_prior_id?: string | null;
  }>>("/team/org/credential", {
    method: "POST",
    body: {},
    ...(signal === undefined ? {} : { signal }),
  });
  const d = body.data ?? {};
  const vs = d.verification_status;
  const verificationStatus: OrgVerificationStatus =
    vs === "verification_pending" || vs === "verified" || vs === "verification_failed" ||
    vs === "verification_expired" || vs === "verification_revoked"
      ? vs
      : "not_verified";
  return {
    credentialId: typeof d.credential_id === "string" ? d.credential_id : "",
    kycStatus: "unverified",
    verificationStatus,
    reissued: d.reissued === true,
    revokedPriorId: typeof d.revoked_prior_id === "string" && d.revoked_prior_id.length > 0 ? d.revoked_prior_id : null,
  };
}

/** One field line of the GLEIF match report the verify endpoint returns. */
export interface VerificationFieldLine {
  readonly field: string;
  readonly label: string;
  readonly expected: string;
  readonly found: string;
  readonly status: "match" | "mismatch" | "missing";
  readonly required: boolean;
}

/** The outcome of applying for organization verification against GLEIF. */
/** The raw GLEIF request/response the admin can inspect via the error's Debug toggle. */
export interface VerificationDebug {
  readonly request: {
    readonly method: string;
    readonly url: string;
    readonly headers: Record<string, string>;
    readonly org: Record<string, unknown>;
  };
  readonly response: {
    readonly status: number;
    readonly headers: Record<string, string>;
    readonly meta: unknown;
    readonly matched: boolean;
    readonly record: unknown;
    readonly totalCandidates: number;
  };
}

/** One registry's compact corroboration row (supplementary to the authoritative GLEIF match). */
export interface CorroborationHit {
  readonly provider: string;
  readonly legalName: string;
  readonly country?: string;
  readonly registrationNumber?: string;
  readonly status: string;
  readonly sourceUrl?: string;
}

/** The multi-registry corroboration summary (never authoritative; GLEIF decides verified). */
export interface VerificationCorroboration {
  readonly resolvedCountry?: string;
  readonly providersAttempted: readonly string[];
  readonly providerStatuses: readonly { readonly provider: string; readonly status: string; readonly httpStatus?: number; readonly latencyMs: number; readonly error?: string }[];
  readonly matches: readonly CorroborationHit[];
  readonly conflicts: readonly string[];
  readonly activeInAnyRegistry: boolean;
  readonly coverageGaps: readonly { readonly provider: string; readonly reason: string }[];
}

/**
 * The outcome of applying for organization verification (org-credential-lifecycle Req 4).
 *
 * Both arms now carry the org-credential assurance so the Members page can update its badge and id
 * from the verify response alone, without a separate refetch:
 *   - On `"verified"`: the credential was revoked-and-reissued as a verified credential, so
 *     `assurance` is `"verified"`, `verificationStatus` is `verified`, `credentialId` is the NEW
 *     verified credential's id, and `revokedPriorId` is the prior (unverified) credential's id.
 *   - On `"verification_failed"`: no issue/revoke happened; the current unverified credential stays,
 *     so `assurance` is `"failed"`, `verificationStatus` is `not_verified`, `credentialId` is the
 *     unchanged current credential's id (or `null`), and `revokedPriorId` is `null`.
 */
export type VerificationResult =
  | {
      readonly status: "verified";
      readonly lei: string;
      readonly credentialId: string;
      readonly reverify: boolean;
      readonly report: readonly VerificationFieldLine[];
      readonly corroboration: VerificationCorroboration | null;
      readonly assurance: OrgAssurance;
      readonly verificationStatus: OrgVerificationStatus;
      readonly revokedPriorId: string | null;
    }
  | {
      readonly status: "verification_failed";
      readonly summary: string;
      readonly report: readonly VerificationFieldLine[];
      readonly debug: VerificationDebug | null;
      readonly corroboration: VerificationCorroboration | null;
      readonly assurance: OrgAssurance;
      readonly verificationStatus: OrgVerificationStatus;
      readonly credentialId: string | null;
      readonly revokedPriorId: string | null;
    };

function parseDebug(raw: unknown): VerificationDebug | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const req = (o["request"] ?? {}) as Record<string, unknown>;
  const resp = (o["response"] ?? {}) as Record<string, unknown>;
  const headers = (h: unknown): Record<string, string> => {
    if (typeof h !== "object" || h === null) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(h as Record<string, unknown>)) if (typeof v === "string") out[k] = v;
    return out;
  };
  return {
    request: {
      method: typeof req["method"] === "string" ? req["method"] : "GET",
      url: typeof req["url"] === "string" ? req["url"] : "",
      headers: headers(req["headers"]),
      org: (typeof req["org"] === "object" && req["org"] !== null ? req["org"] : {}) as Record<string, unknown>,
    },
    response: {
      status: typeof resp["status"] === "number" ? resp["status"] : 0,
      headers: headers(resp["headers"]),
      meta: resp["meta"] ?? null,
      matched: resp["matched"] === true,
      record: resp["record"] ?? null,
      totalCandidates: typeof resp["totalCandidates"] === "number" ? resp["totalCandidates"] : 0,
    },
  };
}

function parseCorroboration(raw: unknown): VerificationCorroboration | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const strArr = (v: unknown): string[] => Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  const matches = (Array.isArray(o["matches"]) ? o["matches"] : []).map((m) => {
    const r = (m ?? {}) as Record<string, unknown>;
    return {
      provider: typeof r["provider"] === "string" ? r["provider"] : "",
      legalName: typeof r["legalName"] === "string" ? r["legalName"] : "",
      ...(typeof r["country"] === "string" ? { country: r["country"] } : {}),
      ...(typeof r["registrationNumber"] === "string" ? { registrationNumber: r["registrationNumber"] } : {}),
      status: typeof r["status"] === "string" ? r["status"] : "unknown",
      ...(typeof r["sourceUrl"] === "string" ? { sourceUrl: r["sourceUrl"] } : {}),
    };
  });
  const providerStatuses = (Array.isArray(o["providerStatuses"]) ? o["providerStatuses"] : []).map((p) => {
    const r = (p ?? {}) as Record<string, unknown>;
    return {
      provider: typeof r["provider"] === "string" ? r["provider"] : "",
      status: typeof r["status"] === "string" ? r["status"] : "",
      ...(typeof r["httpStatus"] === "number" ? { httpStatus: r["httpStatus"] } : {}),
      latencyMs: typeof r["latencyMs"] === "number" ? r["latencyMs"] : 0,
      ...(typeof r["error"] === "string" ? { error: r["error"] } : {}),
    };
  });
  const coverageGaps = (Array.isArray(o["coverageGaps"]) ? o["coverageGaps"] : []).map((g) => {
    const r = (g ?? {}) as Record<string, unknown>;
    return { provider: typeof r["provider"] === "string" ? r["provider"] : "", reason: typeof r["reason"] === "string" ? r["reason"] : "" };
  });
  return {
    ...(typeof o["resolvedCountry"] === "string" ? { resolvedCountry: o["resolvedCountry"] } : {}),
    providersAttempted: strArr(o["providersAttempted"]),
    providerStatuses,
    matches,
    conflicts: strArr(o["conflicts"]),
    activeInAnyRegistry: o["activeInAnyRegistry"] === true,
    coverageGaps,
  };
}

function parseReport(raw: unknown): readonly VerificationFieldLine[] {
  if (!Array.isArray(raw)) return [];
  const out: VerificationFieldLine[] = [];
  for (const r of raw) {
    const o = (r ?? {}) as Record<string, unknown>;
    const status = o["status"];
    out.push({
      field: typeof o["field"] === "string" ? o["field"] : "",
      label: typeof o["label"] === "string" ? o["label"] : "",
      expected: typeof o["expected"] === "string" ? o["expected"] : "",
      found: typeof o["found"] === "string" ? o["found"] : "",
      status: status === "match" || status === "mismatch" || status === "missing" ? status : "missing",
      required: o["required"] === true,
    });
  }
  return out;
}

/**
 * Apply for organization verification against GLEIF (admin only;
 * `POST /api/v1/team/org/credential/verify`). This is the credential-owning verify route
 * (org-credential-lifecycle task 12.2): on a GLEIF `verified` match core/AIS/VCI revoke-and-reissue
 * the org credential as a VERIFIED credential (`kyc_status: "verified"`) and make it current; a
 * non-match records `verification_failed` with no issue/revoke. Resolves to a typed
 * {@link VerificationResult} (verified + the NEW credential id, or failed + the per-field report). A
 * 503 (`verification_unavailable` — GLEIF unreachable, no state change) is thrown as an ApiError so
 * the caller can offer a retry.
 */
export async function applyForOrgVerification(signal?: AbortSignal): Promise<VerificationResult> {
  const body = await apiFetch<ManagementEnvelope<{
    status?: string; lei?: string; credential_id?: string; reverify?: boolean;
    summary?: string; report?: unknown; debug?: unknown; corroboration?: unknown;
    verification_status?: string; revoked_prior_id?: string | null;
  }>>("/team/org/credential/verify", {
    method: "POST",
    ...(signal === undefined ? {} : { signal }),
  });
  const d = body.data ?? {};
  const revokedPriorId =
    typeof d.revoked_prior_id === "string" && d.revoked_prior_id.length > 0 ? d.revoked_prior_id : null;
  if (d.status === "verified") {
    // The verified arm always resolves to `verified` per the design's verify endpoint (Req 4.3).
    return {
      status: "verified",
      lei: typeof d.lei === "string" ? d.lei : "",
      credentialId: typeof d.credential_id === "string" ? d.credential_id : "",
      reverify: d.reverify === true,
      report: parseReport(d.report),
      corroboration: parseCorroboration(d.corroboration),
      assurance: "verified",
      verificationStatus: "verified",
      revokedPriorId,
    };
  }
  const failedStatus: OrgVerificationStatus =
    d.verification_status === "verification_failed" || d.verification_status === "verification_expired" ||
    d.verification_status === "verification_revoked"
      ? d.verification_status
      : "not_verified";
  return {
    status: "verification_failed",
    summary: typeof d.summary === "string" ? d.summary : "The organization did not match the GLEIF register.",
    report: parseReport(d.report),
    debug: parseDebug(d.debug),
    corroboration: parseCorroboration(d.corroboration),
    assurance: assuranceFromStatus(failedStatus),
    verificationStatus: failedStatus,
    credentialId: typeof d.credential_id === "string" && d.credential_id.length > 0 ? d.credential_id : null,
    revokedPriorId,
  };
}

/** Cancel a pending organization verification request (admin only). Returns to not_verified. */
export async function cancelOrgVerification(signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>("/team/org/verify/cancel", {
    method: "POST",
    ...(signal === undefined ? {} : { signal }),
  });
}

/** The stable query key for the current team's organization. */
export const ORG_KEY = ["team", "org"] as const;

// -------------------------------------------------------------------------------------------------
// Team SSO (OIDC) configuration (admin-only writes). The client secret is write-only: it is sent on
// save but never returned by the server (`hasSecret` tells the UI one is already stored).

/** The known SSO providers the UI offers; `other` lets an admin name an unlisted provider. */
export type SsoProviderId = "entra" | "okta" | "1kosmos" | "google" | "other";

/** A team's SSO configuration as the UI reads it (no secret). */
export interface TeamSso {
  readonly providerId: SsoProviderId;
  readonly providerLabel: string;
  readonly providerLogoUrl: string | null;
  readonly issuer: string;
  readonly clientId: string;
  readonly hasSecret: boolean;
  readonly redirectUri: string;
  readonly scope: string;
  readonly enforced: boolean;
  readonly discoveryOk: boolean;
}

/** The body an admin submits to save SSO config. `clientSecret` omitted on update keeps the stored one. */
export interface TeamSsoInput {
  readonly providerId: SsoProviderId;
  readonly providerLabel: string;
  readonly providerLogoUrl?: string;
  readonly issuer: string;
  readonly clientId: string;
  readonly clientSecret?: string;
  readonly redirectUri: string;
  readonly scope: string;
  readonly enforced: boolean;
}

function parseTeamSso(data: unknown): TeamSso | null {
  if (typeof data !== "object" || data === null) return null;
  const r = (data as { sso?: unknown }).sso;
  if (typeof r !== "object" || r === null) return null;
  const o = r as Record<string, unknown>;
  const known: readonly SsoProviderId[] = ["entra", "okta", "1kosmos", "google", "other"];
  const pid = known.includes(o["provider_id"] as SsoProviderId) ? (o["provider_id"] as SsoProviderId) : "other";
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  return {
    providerId: pid,
    providerLabel: str(o["provider_label"]),
    providerLogoUrl: typeof o["provider_logo_url"] === "string" ? (o["provider_logo_url"] as string) : null,
    issuer: str(o["issuer"]),
    clientId: str(o["client_id"]),
    hasSecret: o["has_secret"] === true,
    redirectUri: str(o["redirect_uri"]),
    scope: str(o["scope"]),
    enforced: o["enforced"] === true,
    discoveryOk: o["discovery_ok"] === true,
  };
}

/** Read the current team's SSO config (null when none; admin+ to see). */
export async function getTeamSso(signal?: AbortSignal): Promise<TeamSso | null> {
  const body = await apiFetch<ManagementEnvelope<unknown>>("/team/sso", {
    ...(signal === undefined ? {} : { signal }),
  });
  return parseTeamSso(body?.data ?? null);
}

/** Save (create or update) the current team's SSO config (admin-only). */
export async function saveTeamSso(input: TeamSsoInput, signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>("/team/sso", {
    method: "PUT",
    body: {
      provider_id: input.providerId,
      provider_label: input.providerLabel,
      ...(input.providerLogoUrl !== undefined ? { provider_logo_url: input.providerLogoUrl } : {}),
      issuer: input.issuer,
      client_id: input.clientId,
      ...(input.clientSecret !== undefined && input.clientSecret !== "" ? { client_secret: input.clientSecret } : {}),
      redirect_uri: input.redirectUri,
      scope: input.scope,
      enforced: input.enforced,
    },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Disable (delete) the current team's SSO config (admin-only). */
export async function disableTeamSso(signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>("/team/sso", {
    method: "DELETE",
    ...(signal === undefined ? {} : { signal }),
  });
}

/** The result of a TEST SSO PROVIDER connection check (issue 3). */
export type TeamSsoTestResult =
  | { readonly ok: true; readonly issuer: string; readonly authorizationEndpoint: string; readonly tokenEndpoint: string; readonly jwksUri: string }
  | { readonly ok: false; readonly reason: string };

/**
 * Test the OIDC connection for a candidate issuer WITHOUT saving (admin-only). The server runs
 * discovery against the issuer's /.well-known/openid-configuration and reports whether it resolves.
 */
export async function testTeamSso(issuer: string, signal?: AbortSignal): Promise<TeamSsoTestResult> {
  const body = await apiFetch<ManagementEnvelope<{
    ok?: boolean; reason?: string; issuer?: string;
    authorization_endpoint?: string; token_endpoint?: string; jwks_uri?: string;
  }>>("/team/sso/test", {
    method: "POST",
    body: { issuer },
    ...(signal === undefined ? {} : { signal }),
  });
  const d = body.data ?? {};
  if (d.ok === true && typeof d.authorization_endpoint === "string" && typeof d.token_endpoint === "string" && typeof d.jwks_uri === "string") {
    return {
      ok: true,
      issuer: typeof d.issuer === "string" ? d.issuer : issuer,
      authorizationEndpoint: d.authorization_endpoint,
      tokenEndpoint: d.token_endpoint,
      jwksUri: d.jwks_uri,
    };
  }
  return { ok: false, reason: typeof d.reason === "string" ? d.reason : "The SSO provider test failed." };
}

/** The stable query key for the current team's SSO config. */
export const TEAM_SSO_KEY = ["team", "sso"] as const;

// -------------------------------------------------------------------------------------------------
// SSO access requests (team admin): list pending self-provisioned SSO users; approve / deny.

/** A pending SSO access request as the admin UI lists it. */
export interface AccessRequest {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string | null;
}

/** List pending SSO access requests for the current team (admin-only). */
export async function listAccessRequests(signal?: AbortSignal): Promise<readonly AccessRequest[]> {
  const body = await apiFetch<ManagementEnvelope<{ requests?: unknown }>>("/team/access-requests", {
    ...(signal === undefined ? {} : { signal }),
  });
  const rows = body?.data?.requests;
  if (!Array.isArray(rows)) return [];
  const out: AccessRequest[] = [];
  for (const raw of rows) {
    if (typeof raw !== "object" || raw === null) continue;
    const r = raw as Record<string, unknown>;
    if (typeof r["user_id"] !== "string") continue;
    out.push({
      userId: r["user_id"],
      email: typeof r["email"] === "string" ? r["email"] : "",
      displayName: typeof r["display_name"] === "string" ? r["display_name"] : null,
    });
  }
  return out;
}

/** Approve a pending access request, assigning a role (default viewer). Admin-only. */
export async function approveAccessRequest(userId: string, role: MemberRole, signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>(`/team/access-requests/${encodeURIComponent(userId)}/approve`, {
    method: "POST",
    body: { role },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Deny (remove) a pending access request. Admin-only. */
export async function denyAccessRequest(userId: string, signal?: AbortSignal): Promise<void> {
  await apiFetch<ManagementEnvelope<unknown>>(`/team/access-requests/${encodeURIComponent(userId)}/deny`, {
    method: "POST",
    ...(signal === undefined ? {} : { signal }),
  });
}

/** The stable query key for the team's pending SSO access requests. */
export const ACCESS_REQUESTS_KEY = ["team", "access-requests"] as const;

// -------------------------------------------------------------------------------------------------
// Per-team ban / unban / banned-list (per-team-signin-factors task 10/12). Admin-only, scoped to the
// caller's own selected team T (the server enforces; a non-admin or cross-team target is refused).
// Ban/unban hit `POST /api/v1/team/members/{userId}/{ban|unban}`; the banned list is
// `GET /api/v1/team/banned`. The server answers a guard rejection with the AgentDNA envelope
// `{ status:false, message:<code> }`, so `apiFetch` carries the code on `ApiError.message`; these
// wrappers map the known codes to a typed {@link BanErrorCode} the admin screens branch on without
// parsing status/message everywhere.

/** A banned member row as the admin banned-users list consumes it. */
export interface BannedMember {
  /** The opaque user id (the id used in the unban path). */
  readonly userId: string;
  /** The member's email, for display. */
  readonly email: string;
  /** An optional friendly name. */
  readonly displayName: string | null;
  /** When the ban was applied (epoch ms), when the server provides it. */
  readonly bannedAt: number | null;
  /** The banning admin's user id, when the server provides it. */
  readonly bannedBy: string | null;
}

/**
 * The typed error a ban/unban guard rejection maps to (per-team-signin-factors R12):
 *   - `cannot_ban_self` — an admin cannot ban their own membership (400);
 *   - `last_admin`      — the target is the only active non-banned admin (409);
 *   - `not_bannable`    — no row, already banned, or a cross-team target for a ban (409);
 *   - `not_banned`      — no row or a not-banned target for an unban (409);
 *   - `unknown`         — any other transport/HTTP failure (surface generically).
 */
export type BanErrorCode = "cannot_ban_self" | "last_admin" | "not_bannable" | "not_banned" | "unknown";

/** A ban/unban guard rejection carrying the typed {@link BanErrorCode}. */
export class BanError extends Error {
  readonly code: BanErrorCode;
  constructor(code: BanErrorCode, cause: ApiError) {
    super(cause.message, { cause });
    this.name = "BanError";
    this.code = code;
  }
}

const BAN_ERROR_CODES: readonly string[] = ["cannot_ban_self", "last_admin", "not_bannable", "not_banned"];

/**
 * Map an {@link ApiError} from a ban/unban call to a {@link BanError}. The server encodes the guard
 * code in the envelope `message` (`apiFetch` surfaces it on `error.message`), so we recognise the
 * known codes there; anything else becomes `unknown`.
 */
function toBanError(error: unknown): BanError {
  if (error instanceof ApiError) {
    const code = BAN_ERROR_CODES.includes(error.message) ? (error.message as BanErrorCode) : "unknown";
    return new BanError(code, error);
  }
  // A non-ApiError (should not happen through apiFetch) → a generic unknown wrapper.
  return new BanError("unknown", new ApiError("malformed", String(error)));
}

/**
 * Ban a member of the current team (`POST /api/v1/team/members/{userId}/ban`; admin-only). The ban
 * leaves the member's role/status untouched and only sets the ban columns server-side. Throws a
 * {@link BanError} carrying the typed guard code (`cannot_ban_self` / `last_admin` / `not_bannable`)
 * on a rejection.
 */
export async function banMember(userId: string, signal?: AbortSignal): Promise<void> {
  try {
    await apiFetch<ManagementEnvelope<unknown>>(`/team/members/${encodeURIComponent(userId)}/ban`, {
      method: "POST",
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (e) {
    throw toBanError(e);
  }
}

/**
 * Unban a member of the current team (`POST /api/v1/team/members/{userId}/unban`; admin-only). Unban
 * clears the ban columns and resets the row to `viewer`/`pending_approval` server-side (access must
 * be re-approved). Throws a {@link BanError} with `not_banned` when there is no banned row to clear.
 */
export async function unbanMember(userId: string, signal?: AbortSignal): Promise<void> {
  try {
    await apiFetch<ManagementEnvelope<unknown>>(`/team/members/${encodeURIComponent(userId)}/unban`, {
      method: "POST",
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (e) {
    throw toBanError(e);
  }
}

/** Shape one raw banned-member row (`data.banned[]`) defensively; `null` when it lacks a usable id. */
function parseBannedMember(raw: unknown): BannedMember | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r["user_id"] !== "string" || r["user_id"].length === 0) return null;
  return {
    userId: r["user_id"],
    email: typeof r["email"] === "string" ? r["email"] : "",
    displayName: typeof r["display_name"] === "string" ? r["display_name"] : null,
    bannedAt: typeof r["banned_at"] === "number" && Number.isFinite(r["banned_at"]) ? r["banned_at"] : null,
    bannedBy: typeof r["banned_by"] === "string" ? r["banned_by"] : null,
  };
}

/**
 * List the current team's banned members (`GET /api/v1/team/banned`; admin-only). Scoped to the
 * session tenant; `[]` when none. Unusable rows are dropped (fail closed).
 */
export async function listBannedMembers(signal?: AbortSignal): Promise<readonly BannedMember[]> {
  const body = await apiFetch<ManagementEnvelope<{ banned?: unknown }>>("/team/banned", {
    ...(signal === undefined ? {} : { signal }),
  });
  const rows = body?.data?.banned;
  if (!Array.isArray(rows)) return [];
  const out: BannedMember[] = [];
  for (const raw of rows) {
    const m = parseBannedMember(raw);
    if (m !== null) out.push(m);
  }
  return out;
}

/** The stable query key for the current team's banned-members list. */
export const BANNED_MEMBERS_KEY = ["team", "banned"] as const;
